import Vue from 'vue';
import Component from 'vue-class-component';
import Moment from 'moment';
import KEYS from '@/values/keys';
import { ISchoolDayCalendarEvents } from '@/tools/interfaces/school-day-calendar-events.interface';
import { ISchoolDayCategory } from '@/tools/interfaces/school-day-category.interface';
import { ISchoolDayCalendarEventItem } from '@/tools/interfaces/school-day-calendar-event-item.interface';
import { ISchoolDayCategoryEventItem } from '@/tools/interfaces/school-day-category-event-item.interface';
import { ICreateSchoolDayModalParams } from '@/tools/interfaces/create-school-day-modal-params.interface';
import { _SCHOOL_CALENDAR_STORE, SCHOOL_DAY_STATUS, SCHOOL_DATE_TYPE, SCHOOL_DAYS_EVENTS, SCHOOL_DATE_TYPE_EVENT_CLASSES, COLOR_PICKER_VALUES } from '@/tools/values/school-calendar.values';
import { Status } from '@/models/status.model';
import EdCreateSchoolDay from '@/tools/components/ed-create-school-day/ed-create-school-day.component.vue';
import EdConfigWorkingDays from '@/tools/components/ed-config-working-days/ed-config-working-days.component.vue';
import ToolService from '@/providers/tools';
import { Holiday } from '@/tools/models/holiday.model';
import { SCHOOL_CALENDAR_STORE } from '@/tools/store/school-calendar.store';
import { SchoolDay } from '@/tools/models/school-day.model';
import { Emit, Prop, Ref, Watch } from 'vue-property-decorator';
import { BUILDER_EVENT_TYPES } from '@/courses/values/courses.values';
import { BuilderLesson, BuilderSchoolDaySlot } from '@/app/private/subjects/models/builder.model';
import EdGenericCalendar from '@/core/components/ed-generic-calendar/ed-generic-calendar.component.vue';
import { lightOrDarkFromColor } from '@/utils/color-utils';

@Component({
  name: 'ed-school-calendar',
  components: {
    EdCreateSchoolDay,
    EdConfigWorkingDays,
    EdGenericCalendar,
  }
})
export default class EdSchoolCalendar extends Vue {

  private readonly COLOR_TRANSPARENCY_FACTOR: number = 20;
  private readonly SPLIT_MONTHS: Record<number, string> = {
    0: 'january',
    1: 'february',
    2: 'march',
    3: 'april',
    4: 'may',
    5: 'june',
    6: 'july',
    7: 'august',
    8: 'september',
    9: 'october',
    10: 'november',
    11: 'december'
  };

  @Prop({default: () => ({
    showSidebar: true,
    eventHeight: 10,
    showTabs: true,
    showToday: true,
    showNavigator: true,
    showWorkingDays: true
  }), required: false}) readonly config : {
    showSidebar: boolean, 
    eventHeight: number, 
    showTabs: boolean, 
    showToday: boolean, 
    showNavigator: boolean,
    showWorkingDays: boolean};
  @Prop({required: true}) status : Status;
  @Prop({required: false}) calendarStartYear : number;
  @Prop({required: false, default: () => []}) taskEvents : any;
  @Prop({required: true}) readonly schoolDaysInput: any;
  @Prop() readonly saveWorkingDaysAction: Function;
  @Prop() readonly refreshCalendarAction: Function;
  @Prop() readonly workingDaysInput: any;
  @Prop() readonly enabledWorkingDays: any;
  @Prop() readonly weekdays: any;
  @Prop({ required: true, default: () => [] }) holidaysInput: any;
  @Prop({ required: false, default: null }) readonly startDateInput: string;
  @Prop({ required: false, default: null }) readonly endDateInput: string;
  @Prop({ default: false }) readonly canMoveEvents: boolean;
  @Prop() readonly movingEvents: Function;
  @Prop() schoolDaysSlotsMap: Map<string, BuilderSchoolDaySlot>;
  @Prop() allLessonsMap : Map<number, BuilderLesson>;
  @Prop({ default: false }) readonly showDuration: boolean;
  @Prop({ default: true }) readonly showEvents: boolean;
  @Prop({ default: true }) readonly hideSummary: boolean;
  @Prop({ required: false, default: true }) readonly currentDayBackgroundColor: boolean;
  @Prop({ required: false, default: true }) readonly currentDayButtonColor: boolean;
  @Prop({ required: false, default: () => {} }) readonly customEventColors: Record<string, string>;
  @Prop({ required: false }) readonly applySchoolDayColor: Function;
  @Prop({ required: false, default: false }) readonly draggableEvents: boolean;
  @Prop({ required: false, default: null }) readonly draggableDates: string[];
  @Prop({ required: false, default: false }) readonly currentMonthAutoScroll: boolean;

  @Ref('calendar') readonly calendarInstance: any;

  public months: any[] = [];
  public isSidebarOpened: boolean = true;
  public yearSelected = new Date().getFullYear();
  public startSchoolMonth: string = '08';
  public endSchoolMonth: string = '07';
  public isMobile: boolean = false;
  public calendarHeight: number = 0;
  public toEditColorId: string = '';
  public toEditColorValue: string = '';
  public toEditColorValueAux: string = '';
  public savingColor: boolean = false;
  public viewType : string = 'month';
  public focus : string = '';
  public monthName : string = '';
  public startViewMonth : string = Moment().format('YYYY-MM-DD');
  public type: any = 'year';
  public flattenedEvents: any = [];

  // drag & drop variables
  public value = '';
  public movedEvents = null;
  public dragEvent = null;
  public dragStart = null;
  public dragTime = null;
  public eventToMove = null;
  public isMoveEventConfirmModalShown = false;

  public trackedCalendarDate: Date | null = null;

  get types () : any[] {
    return [
      {
        value: 'year',
        label: this.$t('calendar.data.year')
      },
      {
        value: 'month',
        label: this.$t('calendar.data.monthly')
      },
      {
        value: 'week',
        label: this.$t('calendar.data.weekly')
      },
      {
        value: 'day',
        label: this.$t('calendar.data.day')
      }
    ]
  }

  get organizationId() {
    let institutions: any[] = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get colors() {
    return this.$store.state.SchoolCalendarStore.preferences.colors;
  }

  get startDate() {
    return this.startDateInput ?  this.startDateInput : `${this.yearSelected}-${this.startSchoolMonth}-01`;
  }

  get endDate() {
    return  this.endDateInput ?  this.endDateInput : `${(this.yearSelected + 1)}-${this.endSchoolMonth}-31`;
  }

  get endYear() {
    return this.endDate.substr(0, 4);
  }

  get startYear() {
    return this.startDate.substr(0, 4);
  }

  get sidebarShown() {
    return this.$store.state.AppStore.sidebarShown;
  }

  get schoolCalendarClasses() {
    if(window.innerWidth <= 768 && this.sidebarShown) {
      this.isSidebarOpened = false;
    }
    return {
      'school-calendar--right-sidebar-closed': !this.isSidebarOpened || !this.config.showSidebar,
      'school-calendar--right-sidebar-opened': this.isSidebarOpened,
      'school-calendar--left-sidebar-closed': !this.sidebarShown,
      'school-calendar--left-sidebar-opened': this.sidebarShown,
      'h-full': this.status.error
    }
  }

  get calendarLoading() {
    return Array(12).fill({}).map((x,i)=>i);
  }

  get mapLoading() {
    return Array(4).fill({}).map((x,i)=>i);
  }

  get schoolCalendarData() {
    return {
      organizationId: this.organizationId,
      startDate: this.startDate,
      year: '',
      endDate: this.endDate
    }
  }

  get startSchoolCalendarDay() {
    return Moment(this.events.find(el => el.subType == BUILDER_EVENT_TYPES.FIRST_SCHOOL_CALENDAR)?.start).format('YYYY-MM-DD');
  }

  get endSchoolCalendarDay() {
    return Moment(this.events.find(el => el.subType == BUILDER_EVENT_TYPES.LAST_SCHOOL_CALENDAR)?.start).format('YYYY-MM-DD');
  }

  /* Categories */
  get USHolidays(): ISchoolDayCategory {
    let holidays: Holiday[] = this.holidaysInput;
    let officials = holidays.filter(h => h.official);
    let color = this.getEditingColor(
      SCHOOL_DATE_TYPE.US_HOLIDAYS,
      this.toEditColorId,
      this.colors.US_HOLIDAYS
    );
    return {
      name: String(this.$i18n.t('tools.schooldays.usHolidays')),
      color,
      id: SCHOOL_DATE_TYPE.US_HOLIDAYS,
      events: officials.map(o => {
        return {
          startDate: o.startDate,
          endDate: o.endDate || o.startDate,
          name: o.name,
          type: SCHOOL_DATE_TYPE.US_HOLIDAYS,
          id: o.holidayPlanId,
          status: o.status,
          official: o.official
        }
      })
    }
  }

  get holidays(): ISchoolDayCategory {
    let holidays: Holiday[] = this.holidaysInput;
    let observed = holidays.filter(h => h.status == SCHOOL_DAY_STATUS.OBSERVED && !h.official);
    let color = this.getEditingColor(
      SCHOOL_DATE_TYPE.DAY_OFF,
      this.toEditColorId,
      this.colors.DAY_OFF
    );
    return {
      name: String(this.$i18n.t('tools.schooldays.offDays')),
      color,
      id: SCHOOL_DATE_TYPE.DAY_OFF,
      events: observed.map(o => {
        return {
          startDate: o.startDate,
          endDate: o.endDate || o.startDate,
          name: o.name,
          type: SCHOOL_DATE_TYPE.DAY_OFF,
          id: o.holidayPlanId,
          status: o.status,
          official: o.official
        }
      })
    }
  }

  get schoolDays(): ISchoolDayCategory[] {
    let schooldaysStore = this.schoolDaysInput;
    let schooldays = schooldaysStore ? schooldaysStore : [];
    return schooldays.map(sd => {
      let color = this.getEditingColor(
        String(sd.schoolDayId),
        this.toEditColorId,
        sd.color
      );
      return {
        name: sd.description,
        color,
        dates: sd.dates,
        id: String(sd.schoolDayId),
        events: [{
          startDate: sd.startDate,
          endDate: sd.endDate,
          name: '',
          type: SCHOOL_DATE_TYPE.SCHOOL_CALENDAR,
          category: sd.description,
          id: sd.schoolDayId,
          official: false
        }]
      }
    });
  }

  get currentDate(): ISchoolDayCategory {
    let color = this.getEditingColor(
      SCHOOL_DATE_TYPE.CURRENT_DATE,
      this.toEditColorId,
      this.colors.CURRENT_DATE
    );
    return {
      name: String(this.$i18n.t('tools.schooldays.today')),
      color,
      id: SCHOOL_DATE_TYPE.CURRENT_DATE,
      events: [{
        startDate: Moment().format('YYYY/MM/DD'),
        endDate: Moment().format('YYYY/MM/DD'),
        name: '',
        notEdit: true,
      }]
    }
  }

  get nonSchoolDay(): ISchoolDayCategory {
    let nonSchoolDay = this.holidaysInput.filter(h => h.status == SCHOOL_DAY_STATUS.NOTIFIED);
    let color = this.getEditingColor(
      SCHOOL_DATE_TYPE.EVENTS,
      this.toEditColorId,
      this.colors.EVENTS
    );
    return {
      name: String(this.$i18n.t('tools.schooldays.events')),
      color,
      id: SCHOOL_DATE_TYPE.EVENTS,
      events: nonSchoolDay.map(o => {
        return {
          startDate: o.startDate,
          endDate: o.endDate || o.startDate,
          name: o.name,
          type: SCHOOL_DATE_TYPE.EVENTS,
          status: o.status,
          id: o.holidayPlanId,
          official: o.official
        }
      })
    }
  }
  /* End Categories */

  get schoolCalendarMap(): ISchoolDayCalendarEvents[] {
    let events = this.nonSchoolDay.events.length > 0 ? this.nonSchoolDay : null;
    let offDays = this.holidays.events.length > 0 ? this.holidays : null;
    let data = [
      this.currentDate,
      ...this.schoolDays
    ];
    if(events) data.push(events);
    if(offDays) data.push(offDays);
    data.push(this.USHolidays);
    return data.map(c => {
      return {
        ...c,
        events: c.events.map(e => {
          let startMonth = Moment(e.startDate).format('MMMM').toLocaleLowerCase();
          let label = `${this.$i18n.t(`common.months.${startMonth}`)} ${Moment(e.startDate).format('DD')}`;
          if(e.startDate !== e.endDate) {
            let endMonth = Moment(e.endDate).format('MMMM').toLocaleLowerCase();
            if(startMonth !== endMonth) {
              label += ` - ${this.$i18n.t(`common.months.${endMonth}`)} ${Moment(e.endDate).format('DD')}`;
            } else {
              label += ` - ${Moment(e.endDate).format('DD')}`;
            }
          }
          label += e.name ? `: ${e.name}` : '';
          return {
            ...e,
            label,
            date: e.startDate,
            start: e.startDate,
            end: e.endDate
          }
        })
      } 
    });
  }

  /* Events */
  get schoolCalendarEvents(): ISchoolDayCalendarEventItem[] {
    let events = [];
    this.schoolDays.forEach(sd => {
      events.push(
        ...this.getCalendarItems(
          sd.events, 
          sd.color,
          true
        )
      );
    });
    events.push(...this.getCalendarItems(
      this.USHolidays.events, this.USHolidays.color
    ));
    events.push(...this.getCalendarItems(
      this.holidays.events, this.holidays.color
    ));
    if(this.showEvents)
      events.push(...this.getCalendarItems(
        this.nonSchoolDay.events, this.nonSchoolDay.color
      ));
    return events;
  }

  get calendarContentStyle() {
    let style: any = {};
    if(this.isMobile) {
      style = {
        height: `${this.calendarHeight}px`,
        overflowY: 'scroll'
      }
    }
    return style;
  }

  // Deprecated?
  get businessDays(): Holiday[] {
    let holidays: Holiday[] = this.$store.state.SchoolCalendarStore.holidays;
    return holidays.filter(h => h.status == SCHOOL_DAY_STATUS.IGNORED);
  }

  get displayYear() : string {
    if (this.type === 'year') {
      return `${this.startYear} - ${this.endYear}`
    } else {
      return String(this.trackedCalendarDate ? this.trackedCalendarDate.getFullYear() : this.yearSelected);
    }
  }

  get events () {
    const processedEvents = [];
    this.taskEvents.forEach(event => {
      let duration =  undefined;
      if(this.showDuration && this.allLessonsMap)
        duration = this.allLessonsMap.get(event.id)?.duration;

      if (event.start === event.end) {
        let className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON
        if(event.type === 'FILLERS')
          className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_FILLERS;
        else if(event.type === 'FUN')
          className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_FUN;
        else if (Moment(event.start).isSameOrBefore(Moment(), 'day')) 
          className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_PAST_DAY;
        let start =  new Date(Number(Moment(event.start).format('YYYY')), Number(Moment(event.start).format('MM'))-1, Number(Moment(event.start).format('DD')))
        let end = new Date(Number(Moment(event.end).format('YYYY')), Number(Moment(event.end).format('MM'))-1, Number(Moment(event.end).format('DD')));
        processedEvents.push({
          ...event,
          label: `${event.label}${duration ? ` (${duration} ${this.$t('courses.createCourse.minutes')})` : ''}`,
          start, 
          end,
          allowDrag: true,
          lessonDates: {start, end},
          className,
        });
      } else {
        processedEvents.push(...splitEvents(event, duration, this));
      }
    });
    return processedEvents.concat(this.schoolCalendarEvents);
    
    /**
     * Splits an event into multiple events, each representing a single day.   
     * @returns {Object[]} An array of event objects, each representing a single day.
     */
    function splitEvents(event, duration, _this){
      const start = Moment(event.start);
      const end = Moment(event.end);
      const inPast = start.isSameOrBefore(Moment(), 'day')
      const days = [];
      for (let m = start; m.isSameOrBefore(end); m.add(1, 'days')) {
        const date = m.format('YYYY-MM-DD');
        const _event = { ...event, 
          start: new Date(date + 'T00:00:00'), 
          end: new Date(date + 'T00:00:00'),
          allowDrag: false,                                   //Just allow to drag the start event (head)
          lessonDates: {start: event.start, end: event.end}   //Keep the original lesson dates 
        }
        let slot = null;
        switch (date) {
          case event.start:
            _event.className = inPast ? SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_PAST_DAY_START : SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_START;
            _event.label = `${_this.$i18n.t('courses.subjectBuilder.lessonStart', {lesson: event.name})} ${duration ? ` (${duration} ${_this.$t('courses.createCourse.minutes')})` : ''}`; 
            _event.allowDrag =  true
            break;
          case event.end:
            _event.name = `${_this.$i18n.t('courses.subjectBuilder.lessonEnd', {lesson: event.name})}`;
            _event.className = inPast ? SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_PAST_DAY_END : SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_END;
            _event.label = `${_this.$i18n.t('courses.subjectBuilder.lessonEndLabel', {lesson: event.name})}`;
            break;
          default:
            _event.name = '. . .';
            slot = _this.schoolDaysSlotsMap.get(date);
            if(_this.getIsSchoolDay(slot)){  
              if(slot.lessons.includes(event.id)){
                _event.className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_SCHOOL_DAY;
                _event.label = `${_this.$i18n.t('courses.subjectBuilder.lessonSchoolDay', {lesson: event.name})}`;
              } else {
                _event.className = SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_NON_SCHOOL_DAY
                _event.label = `${_this.$i18n.t('courses.subjectBuilder.lessonNonTimeDay', {lesson: event.name})}`;
              }
            } else { 
              _event.className = inPast ? SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_PAST_DAY_NON_SCHOOL_DAY : SCHOOL_DATE_TYPE_EVENT_CLASSES.LESSON_NON_SCHOOL_DAY;
              _event.label = `${_this.$i18n.t('courses.subjectBuilder.lessonNonSchoolDay', {lesson: event.name})}`;
            }
            break;
        }
        days.push(_event);
      } 
      return days;
    }
  }

  get eventSuffix(): any {
    return {
      expressions: [],
      events: [],
    };
  }

  get calendarDays(): any {
    const calendarDays = {};

    this.schoolDays?.forEach((schoolDay) => {

      if (!schoolDay.dates) return;

      const schoolDayDetail = {
        color: `${schoolDay.color}${this.COLOR_TRANSPARENCY_FACTOR}`,
      };

      const todayString = Moment().format('YYYY-MM-DD');
      schoolDay.dates.forEach((date) => {
        const dayString = Moment(date).format('YYYY-MM-DD');

        if (this.applySchoolDayColor) {
          calendarDays[dayString] = !this.applySchoolDayColor(dayString) ? {} : {
            color: `#2BB2AC${this.COLOR_TRANSPARENCY_FACTOR}`,
          };
          return;
        }

        if (dayString === todayString) {
          calendarDays[dayString] = this.currentDayBackgroundColor ? {} : schoolDayDetail;
        } else {
          calendarDays[dayString] = schoolDayDetail;
        }
      });
    });

    return calendarDays;
  }

  get calendarEvents(): any[] {
    const toDate = (date: Date | string) => date instanceof Date ? date : Moment(date + 'T00:00:00').toDate();
    const sort = (eventA: any, eventB: any) => {
      const showFirst = [
        SCHOOL_DATE_TYPE.SCHOOL_CALENDAR,
        SCHOOL_DATE_TYPE.US_HOLIDAYS,
        SCHOOL_DATE_TYPE.DAY_OFF,
      ];
      return eventA.type === eventB.type ? 0 : showFirst.includes(eventA.type) ? -1 : 1;
    }

    return this.events?.sort(sort)?.map(event => {
      return {
        ...event,
        color: this.customEventColors ? this.customEventColors[event.type] ?? event.color : event.color,
        uid: `${event.id}`,
        startDate: toDate(event.start),
        endDate: toDate(event.end),
        allDay: !event.timed,
        summary: event.summary ?? event.name ?? event.label,
      };
    });
  }

  get calendarDateString(): string | null {
    return this.trackedCalendarDate ? Moment(this.trackedCalendarDate).format('YYYY-MM-DD') : this.startViewMonth;
  }

  /**
   * Checks if the given date is a school day.
   * Validates if the date is a school day by checking if it exists in the schoolDaysSlotsMap and if its allowedTypes include CONTENT.
   * @param date 
   * @returns 
   */
  getIsSchoolDay(slot : BuilderSchoolDaySlot) {
    if (slot && slot.allowedTypes?.includes(BUILDER_EVENT_TYPES.CONTENT)) 
      return slot.totalTime === null || slot.totalTime > 0;
    return false;
  }

  getEditingColor(categoryId: string, currentEditId: string, colorDefault: string) {
    let isCatEditing = currentEditId == categoryId;
    let color = colorDefault;
    if(isCatEditing && this.toEditColorValue) color = this.toEditColorValue;
    return color;
  }

  getCalendarItems(
    events: ISchoolDayCategoryEventItem[],
    color :string,
    isSchoolCalendar = false): ISchoolDayCalendarEventItem[] {
      let eventResult: ISchoolDayCalendarEventItem[] = [];
      
      if(!isSchoolCalendar) {
        eventResult = events.map(e => {
          const className = SCHOOL_DATE_TYPE_EVENT_CLASSES[e.type];

          return this.getItem({ ...e, className }, color);
        });
      } else {
        events.forEach(e => {
          eventResult = [
            this.getItem({
                ...e,
                startDate: e.startDate,
                endDate: e.startDate,
                name: String(this.$i18n.t('tools.schooldays.firstDay')),
                subType: SCHOOL_DATE_TYPE.FIRST_SCHOOL_CALENDAR,
                className: SCHOOL_DATE_TYPE_EVENT_CLASSES.FIRST_SCHOOL_CALENDAR,
              },
              color
            ),
            this.getItem({
                ...e,
                startDate: e.endDate,
                endDate: e.endDate,
                name: String(this.$i18n.t('tools.schooldays.lastDay')),
                subType: SCHOOL_DATE_TYPE.LAST_SCHOOL_CALENDAR,
                className: SCHOOL_DATE_TYPE_EVENT_CLASSES.LAST_SCHOOL_CALENDAR,
              },
              color
            )
          ]
        });
      }
      return eventResult;
  }

  getItem(e: ISchoolDayCategoryEventItem, color: string): ISchoolDayCalendarEventItem {
    return {
      end: e.endDate,
      start: e.startDate,
      timed: false,
      color,
      label: e.name,
      type: e.type,
      subType: e.subType,
      category: e.category,
      id: e.id,
      status: e.status,
      official: e.official,
      className: e.className,
    }
  }

  toggleSidebar() {
    if(window.innerWidth <= 846 && !this.isSidebarOpened) {
      if(this.sidebarShown) {
        this.$App.Store.toggleSidebar();
      }
    } else {
      this.$App.Store.showSidebar();
    }

    this.isSidebarOpened = !this.isSidebarOpened;
  }

  goToEditEvent(event) {
    let isSchoolCalendar = event.type == SCHOOL_DATE_TYPE.SCHOOL_CALENDAR;
    let dates = isSchoolCalendar ? this.getDatesOfSchoolDay(event.id) : event;
    let description = isSchoolCalendar ? event.category : event.name || event.label;
    let now = Moment(Moment().format('YYYY-MM-DD'));
    let canContinue = Moment(dates.start).isSame(now, 'date') || Moment(dates.end).isSame(now, 'date') || Moment(dates.end).isAfter(now) || Moment(dates.start).isAfter(now, 'date');
    if(canContinue) {
      let params: ICreateSchoolDayModalParams = {
        date: dates ? dates.start : event.start,
        edition: true,
        description,
        id: event.id,
        type: event.type,
        status: event.status,
        endAt: dates ? dates.end : '',
        official: event.official,
      };
      this.$EdModal.createModal(
        EdCreateSchoolDay,
        params
      );
    } else {
      this.showErrorTryCreating();
    }
  }

  showErrorTryCreating() {
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('tools.schooldays.errors.errorSavingDateTitle'),
      body: this.$i18n.t('tools.schooldays.errors.errorTryCreate'),
      type: KEYS.EVENT_TYPES.DANGER
    });
    this.$emit('goToPresentDay');
  }

  openConfigWorkingDays() {
    this.$EdModal.createModal(EdConfigWorkingDays, {
      saveWorkingDaysAction: this.saveWorkingDaysAction,
      refreshCalendarAction: this.refreshCalendarAction,
      workingDaysInput: this.workingDaysInput,
      enabledWorkingDays: this.enabledWorkingDays
    });
  }

  getDatesOfSchoolDay(schoolDayId: number) {
    let schooldays = this.$store.state.SchoolCalendarStore.schooldays;
    let period = schooldays.find(sc => sc.schoolDayId == schoolDayId);
    return { 
      start: period.startDate,
      end: period.endDate
    }
  }

  dayFormat(value) {
    return Moment(value.date).format('DD');
  }

  weekDayFormat(value) {
    let weekday = Moment(value.date).format('dddd').toLowerCase();
    return String(this.$i18n.t(`common.shorWeekdays.${weekday}`)).toUpperCase();
  }

  hideTooltip() {
    this.$EdContextMenu.close();
  }

  setCategoryEditColor(categoryId: string, categoryColor: string) {
    if(!this.savingColor) {
      if(!this.toEditColorId) {
        setTimeout(() => {
          window.addEventListener('click', this.cleanCategoryEditColor);
        }, 100);
      }
      this.toEditColorValue = this.toEditColorValueAux = categoryColor;
      this.toEditColorId = categoryId;
    }
  }

  cleanCategoryEditColor(e) {
    let colorPicker = document.querySelector('.color-picker');
    let isPickerClicked = colorPicker ? colorPicker.contains(e.target) : false;
    let isEditClicked = false;
    document.querySelectorAll('.edit-icon').forEach(icon => {
      if(icon.contains(e.target)) isEditClicked = true;
    });
    if(!isPickerClicked && !isEditClicked && !this.savingColor) {
      this.toEditColorId = null;
      window.removeEventListener('click', this.cleanCategoryEditColor);
    }
  }

  async saveCategoryColor(categoryId:number|string, categoryColorInput:string) {
    try {
      this.savingColor = true;

      // Validate HEX color or save default color
      const validHexRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
      let categoryColor = categoryColorInput;
      if (!validHexRegex.test(categoryColor)) {
        categoryColor = COLOR_PICKER_VALUES.DEFAULT_COLOR;
      }

      if(isNaN(Number(categoryId))) {//when categoryId can be: 'CURRENT_DATE', 'EVENTS', 'DAY_OFF', 'US_HOLIDAYS'
        await this.$store.dispatch(SCHOOL_CALENDAR_STORE.ACTIONS.SAVE_COLORS_PREFERENCE, {
          key: categoryId,
          value: categoryColor
        });
      } else {
        let calendars: SchoolDay[] = this.$store.state.SchoolCalendarStore.schooldays;
        let schoolDay = calendars.find(sd => sd.schoolDayId == Number(categoryId));
        await this.$store.dispatch(SCHOOL_CALENDAR_STORE.ACTIONS.SAVE_SCHOOLDAYS, {
          schoolDayId: schoolDay.schoolDayId,
          organizationId: this.organizationId,
          description: schoolDay.description,
          startDate: schoolDay.startDate,
          endDate: schoolDay.endDate,
          color: categoryColor,
        });
      }
      this.toEditColorId = null;
      this.toEditColorValue = null;

    } catch(err) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('tools.schooldays.errors.errorSavingDateTitle'),
        body: this.$i18n.t('tools.schooldays.errors.errorSavingColor'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this.savingColor = false;
    }
  }

  setEditColor(e) {
    if(e.target.value.length < 7) {
      this.toEditColorValue = this.toEditColorValueAux = COLOR_PICKER_VALUES.DEFAULT_COLOR;
    } else { 
      this.toEditColorValue = e.target.value;
    }
  }

  onColorInput(e) {
    if(e.target.value.length < 7) {
      this.toEditColorValue = COLOR_PICKER_VALUES.DEFAULT_COLOR;
    } else { 
      this.toEditColorValue = this.toEditColorValueAux = e.target.value; 
    }
    
    if (this.toEditColorValueAux.charAt(0) !== '#') {
      this.toEditColorValueAux = `#${this.toEditColorValueAux}`;
    }
  }

  setSchoolCalendar(value: number) {
    this.setYearSelected(this.yearSelected += value);
    this.$emit('refreshCalendar', this.schoolCalendarData, this.organizationId);
    this.changeStartViewMonth(Moment(this.startViewMonth).year(this.yearSelected));
  }

  createCalendar() {
    let startDate = Moment(this.startDate);
    let endDate = Moment(this.endDate);
    let end = Moment(this.startDate);
    this.months = [];
    while (startDate <= endDate) {
      this.months.push({
        start: startDate.format('YYYY-MM-DD'),
        end: end.endOf('month').format('YYYY-MM-DD'),
        name: startDate.format('MMMM').toLowerCase(),
        show: true,
        monthNumber: end.get('month'),
        focus: startDate.format('YYYY-MM-DD')
      });
      startDate.add(1, 'M');
      end.add(1, 'M');
    }
  }

  setYearSelected(yearSelected?: number) : void {

    if (yearSelected) {
      this.yearSelected = yearSelected;
      return;
    }

    if(this.calendarStartYear){
      this.yearSelected = this.calendarStartYear;
      let today = Moment();
      this.changeStartViewMonth(today);
      return;
    }

    const { calendarStartDate } = this.getSchoolCalendarDates(this._getCalendarDate() ?? new Date());
    this.yearSelected = calendarStartDate.getFullYear();
  }

  changeStartViewMonth(date: Moment.Moment) : void {
    this.startViewMonth = date.format('YYYY-MM-DD');
    this.focusDate({date: this.startViewMonth});
  }

  onResize() {
    if(window.innerWidth < 980) {
      this.isSidebarOpened = false;
    }
    if(this.isMobile) {
      let schoolCalendarRect : DOMRect = (this.$refs.schoolCalendar as HTMLElement).getBoundingClientRect();
      this.calendarHeight = window.innerHeight - schoolCalendarRect.top;
    }
  }

  setDevice() {
    this.isMobile = ToolService.getIsMobileDevice();
    if(this.isMobile) {
      document.querySelector('body').style.overflow = 'hidden';
    }
  }

  refreshCalendarEvent() {
    this.setYearSelected();
    this.$emit('refreshCalendar', this.schoolCalendarData, this.organizationId);
  }

  changeType() : void {
    this.flattenedEvents = [];
    switch(this.type){
      case 'year': { 
        this.setYearSelected();
        this.refreshCalendarEvent();
        this.viewType = 'month';
        break;
      }
      default: {
        this.viewType = this.type;
        break;
      }
    }
  }

  focusDate ({ date }) {
    this.focus = date;
  }

  showToday() : void {
    this.focus = '';
    let today = Moment();
    this.changeStartViewMonth(today);
    this._setCalendarDate(today.toDate());
    this._trackCalendarDate(today.toDate());
    this.setYearSelected();
    this.$emit('refreshCalendar', this.schoolCalendarData, this.organizationId);
  }

  prevUniqueCalendar () : void {
    this.calendarInstance?.previousDate();
    this._trackCalendarDate();
  }

  nextUniqueCalendar () : void {
    this.calendarInstance?.nextDate();
    this._trackCalendarDate();
  }

  changeCalendar() : void {
    const date: Date = this._getCalendarDate();
    if (!date) {
      return;
    }

    this.monthName = this.SPLIT_MONTHS[date.getMonth()];
  }

  refreshCalendar() {
    this.showToday();
  }

  // Drag & drop methods
  startDrag ({ event, timed }) {
    if (this.canMoveEvents && event && event.allowDrag) {
      this.dragEvent = event
      this.dragEvent._start = this.dragEvent.start    //The original position of the event
      this.dragEvent._end = this.dragEvent.end
      this.eventToMove = { ...event };
      this.dragTime = null
    }
  }

  startTime (tms) {
    if (this.canMoveEvents) {
      const mouse = this.toTime(tms)
  
      if (this.dragEvent) {
        const start = this.dragEvent.start
        this.dragTime = mouse - start
      } 
    }
  }

  mouseMove (tms) {
    if (this.canMoveEvents) {
      const mouse = this.toTime(tms)
    
      if (this.dragEvent && this.movingEvents) {
        let newStartTime = this.movingEvents({
          mouse, 
          dragTime: this.dragTime, 
          dragEvent: this.dragEvent, 
          startSchoolCalendarDay: this.startSchoolCalendarDay, 
          endSchoolCalendarDay: this.endSchoolCalendarDay
        });

        if(newStartTime) {
          const newStart = this.roundTime(newStartTime);
          this.dragEvent.start = this.dragEvent.end = newStart
        }else{ //Return the event to the original position
          this.dragEvent.start = this.dragEvent._start;
          this.dragEvent.end = this.dragEvent._end;
        }
      }
    }
  }

  endDrag (data) {
    if (this.canMoveEvents) {    
      const eventDateBefore = Moment(this.eventToMove?.start).format('YYYY-MM-DD');
      const eventDateAfter = Moment(data.date).format('YYYY-MM-DD');

      if (this.eventToMove && eventDateBefore !== eventDateAfter) {
        this.$emit('onEventMoved', {
          event: { ...this.eventToMove },
          from: eventDateBefore,
          to: eventDateAfter,
          startSchoolCalendarDay: this.startSchoolCalendarDay, 
          endSchoolCalendarDay: this.endSchoolCalendarDay
        });
      } //TODO PENDING: identify how we can to reload data when the event has more than one day and this was moved to the same day
  
      this.eventToMove = null;
      this.dragTime = null;
      this.dragEvent = null;
    }
  }

  roundTime (time, down = true) {
    const roundTo = 15 // minutes
    const roundDownTime = roundTo * 60 * 1000

    return down
      ? time - time % roundDownTime
      : time + (roundDownTime - (time % roundDownTime))
  }

  toTime (tms) {
    return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
  }

  resetColorPicker(): void {
    this.toEditColorId = null; 
    this.toEditColorValue = null;
  }

  /**
   * Returns the start and end dates of the school calendar based on the given reference date
   * @param referenceDate The reference date to get the school calendar dates
   * @returns The start and end dates of the school calendar
   */
  getSchoolCalendarDates(referenceDate: Date): { calendarStartDate: Date, calendarEndDate: Date } {

    const year = referenceDate.getFullYear();
    const calendarStartDate = Moment(`${year}-${this.startSchoolMonth}-01`);
    const calendarEndDate = Moment(`${(year)}-${this.endSchoolMonth}-31`);

    if (referenceDate < calendarEndDate.toDate()) {
      calendarStartDate.year(year - 1);

    } else {
      calendarEndDate.year(year + 1);
    }

    return {
      calendarStartDate: calendarStartDate.toDate(),
      calendarEndDate: calendarEndDate.toDate()
    };
  }

  @Emit('onWcCalendarDaySelection')
  public onWcCalendarDaySelection(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayContainerClick')
  public onWcCalendarDayContainerClick(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayMouseOver')
  public onWcCalendarDayMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayMouseLeave')
  public onWcCalendarDayMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventSelection')
  public onWcCalendarEventSelection(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventMouseOver')
  public onWcCalendarEventMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventMouseLeave')
  public onWcCalendarEventMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayButtonMouseOver')
  public onWcCalendarDayButtonMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayButtonMouseLeave')
  public onWcCalendarDayButtonMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDateChange')
  public onWcCalendarDateChange(customEvent: CustomEvent): CustomEvent {
    this.changeCalendar();
    return customEvent;
  }

  @Emit('onWcCalendarEventDragStart')
  onWcCalendarEventDragStart(customEvent: CustomEvent): void {
    const event = customEvent?.detail?.value?.eventData?.event;
    if (!event) {
      return;
    }

    this.startDrag({ event, timed: event.timed });
  }

  onWcCalendarEventDragEnd(customEvent: CustomEvent): void {
    const date = customEvent?.detail?.value?.eventData?.date;
    if (!date) {
      return;
    }

    this.endDrag({ date });
  }

  private _setCalendarDate(date: Date): void {
    if (this.calendarInstance?.setCalendarDate) {
      this.calendarInstance?.setCalendarDate(date);
      this._trackCalendarDate();
    }
  }

  private _getCalendarDate(): Date | null {
    return this.calendarInstance?.getCalendarDate ? this.calendarInstance?.getCalendarDate() : null;
  }

  private _trackCalendarDate(date?: Date): void {
    this.trackedCalendarDate = date ?? this._getCalendarDate();
    this.setYearSelected(this.trackedCalendarDate?.getFullYear());
  }

  @Watch('type')
  _onTypeChange(): void {
    this.$nextTick(() => {
      this.changeCalendar();
      if (this.trackedCalendarDate && this.type !== 'year') {
        this.setYearSelected(this.trackedCalendarDate?.getFullYear());
      }
    });
  }

  @Watch('schoolCalendarMap', { deep: true })
  private _setCurrentDayStyle(): void {
    const currentDay = this.schoolCalendarMap.find((item) => item.id === SCHOOL_DATE_TYPE.CURRENT_DATE);
    const elementStyle = (this.$el as HTMLElement).style;

    if (!this.currentDayButtonColor && !this.currentDayBackgroundColor) {
      elementStyle.setProperty(EdGenericCalendar.VAR_CURRENT_DAY_BUTTON_BACKGROUND_COLOR, 'transparent');
      return;
    }

    if (!currentDay?.color || !elementStyle) {
      return;
    }

    const backgroundColor = currentDay.color;
    const isDarkBackground = lightOrDarkFromColor(backgroundColor) === 'dark';
    const fontColor = isDarkBackground ? 'white' : 'black';

    elementStyle.setProperty(EdGenericCalendar.VAR_CURRENT_DAY_FONT_COLOR, fontColor);
    elementStyle.setProperty(EdGenericCalendar.VAR_CURRENT_DAY_BUTTON_BACKGROUND_COLOR, backgroundColor);

    if (this.currentDayBackgroundColor) {
      elementStyle.setProperty(EdGenericCalendar.VAR_CURRENT_DAY_CONTAINER_BACKGROUND_COLOR, backgroundColor);
    }
  }

  @Watch('status', { deep: true })
  private _onStatusChange(): void {
    const displayingCurrentYear = this.yearSelected === Moment().year();
    if (this.status.load && this.type === 'year' && displayingCurrentYear) {
      setTimeout(() => {
        this._scrollToCurrentMonth();
      }, 1000);
    }
  }

  private _scrollToCurrentMonth(): void {
    if (this.type !== 'year' || !this.calendarInstance || !this.currentMonthAutoScroll) return;
    const currentMonth = Number.parseInt(Moment().format('MM')) - 1;
    const monthList: HTMLElement[] = Array.from(this.calendarInstance)?.map((month: any) => month?.$el) ?? [];
    const monthElement = monthList?.find(month => month?.querySelector(`[month="${currentMonth}"]`));
    monthElement?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  created() {
    this.setYearSelected();
    this.onResize();
    this.$emit('refreshCalendar', this.schoolCalendarData, this.organizationId);
    this.$edEventBus.$on(
      SCHOOL_DAYS_EVENTS.REFRESH_CALENDAR, 
      this.refreshCalendarEvent
    );
    window.addEventListener('resize', this.onResize);
  }

  mounted() {
    this.setDevice();
    this.onResize();
    this.$edEventBus.$on('openConfigWorkingDays', this.openConfigWorkingDays);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('click', this.cleanCategoryEditColor);
    document.querySelector('body').style.overflow = 'auto';
    this.$edEventBus.$off('openConfigWorkingDays');
  }

  destroyed() {
    this.$edEventBus.$off(
      SCHOOL_DAYS_EVENTS.REFRESH_CALENDAR, 
      this.refreshCalendarEvent
    );
  }

  @Watch('events', { deep: true })
  setInitMovedEvents() {
    this.movedEvents = this.events;
  }

}